import React, { useCallback, useRef, useEffect, useState, useMemo, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getFeedbackByUuid } from 'Shared/selectors/Feedbacks.selectors';
import Avatar from 'Shared/components/Avatar/Avatar';
import Info from 'Shared/components/Info/Info';
import { getFirstName } from 'Shared/helpers';
import { dateMMMDDYYYY } from 'Shared/utils/DateTime.utils';
import { FeedbackRecognitionType, FeedbackRecognitionRequestTypes } from 'Shared/constants/types';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import GiveRecognitionModal from 'Shared/components/modals/GiveRecognitionModal/GiveRecognitionModal';
import { viewFeedback } from 'Shared/actions/Feedbacks.actions';
import { useFocusOnSeen } from 'Shared/utils/hooks/use-focus-on-seen';
import InviteTeammateButton from 'Shared/components/InviteTeammateButton/InviteTeammateButton';
import * as UsersSelectors from 'Shared/selectors/Users.selectors';

import FeedbackCommentsArea from './FeedbackCommentsArea/FeedbackCommentsArea';
import FeedbackContent from './FeedbackContent/FeedbackContent';
import RecognitionContent from './RecognitionContent/RecognitionContent';
import OptionsButtons from './OptionsButtons/OptionsButtons';
import NotificationAndViewedBar from './NotificationAndViewedBar/NotificationAndViewedBar';
import Reactions from 'Shared/components/Reactions/Reactions';
import { getTitle } from './utils';

import './FeedbackItem.scss';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

type Props = {
  sectionRef: {
    current: HTMLDivElement;
  };
  feedbackRecognitionItemUuid: string;
  isFocused: boolean;
  onDeleteFeedback: Function;
  onEditFeedbackClick: Function;
  onGiveFeedback: Function;
};

function FeedbackItem({
  feedbackRecognitionItemUuid,
  onDeleteFeedback,
  onEditFeedbackClick,
  isFocused,
  onGiveFeedback,
  sectionRef,
}: Props) {
  const { reactionsKudos: showReactions } = useFlags();
  const feedbackRecognitionItem: FeedbackRecognitionType = useDeepEqualSelector(
    getFeedbackByUuid(feedbackRecognitionItemUuid)
  );
  const { id: currentUserId } = useCurrentUser();

  const creator =
    useSelector(state =>
      UsersSelectors.getUserByUserId(state, { userId: feedbackRecognitionItem?.creatorId })
    )?.toJS() || null;
  const receiver =
    useSelector(state =>
      UsersSelectors.getUserByUserId(state, { userId: feedbackRecognitionItem?.receiverId })
    )?.toJS() || null;

  const [isEditKudosModalOpen, setIsEditKudosModalOpen] = useState(false);
  const feedbackItemRef = useRef(null);

  const dispatch = useDispatch();
  const isFeedback = feedbackRecognitionItem.requestType === FeedbackRecognitionRequestTypes.feedback;

  const {
    uuid,
    receiverId,
    creatorId,
    viewerIds,
    workspaceName,
    workspaceUuid,
    feedbackRecognitionRequestId,
  } = feedbackRecognitionItem;

  const feedbackItemIsViewed = useMemo(() => viewerIds.some(item => item === currentUserId), [
    currentUserId,
    viewerIds,
  ]);

  const receiverAndCreatorAreInWorkspace = useSelector(
    state =>
      state.workspaces?.find(({ id }) => id === workspaceUuid)?.users?.find(({ id }) => id === receiverId) &&
      state.workspaces?.find(({ id }) => id === workspaceUuid)?.users?.find(({ id }) => id === creatorId)
  );

  const showGiveFeedbackButton =
    isFeedback &&
    feedbackRecognitionItem.feedbacksCountInFeedbackRequest < 2 &&
    feedbackRecognitionItem.creatorId !== currentUserId;

  const handleViewItem = useCallback(() => {
    // If we're dealing with a FeedbackRequest, then fire feedbackIsViewedAction if the user is not the creator
    // If we're dealing with a RecognitionRequest, then fire the feedbackIsViewedAction if the user is not the creator and not the receiver
    // This is because the receiver for the RecognitionRequest has to scratch it off to get this action.
    if (currentUserId !== creatorId && (isFeedback || currentUserId !== receiverId)) {
      if (!feedbackItemIsViewed) dispatch(viewFeedback(uuid));
    }
  }, [currentUserId, creatorId, isFeedback, receiverId, feedbackItemIsViewed, dispatch, uuid]);

  const { focused } = useFocusOnSeen({
    sectionRef,
    itemRef: feedbackItemRef,
    onFocus: handleViewItem,
    offsetBottom: 100,
  });

  const handleToggleKudosModal = useCallback(() => setIsEditKudosModalOpen(isOpen => !isOpen), []);

  const handleDeleteClick = useCallback(() => {
    if (window.confirm('Are you sure you want to delete this feedback?')) onDeleteFeedback(feedbackRecognitionItem);
  }, [feedbackRecognitionItem, onDeleteFeedback]);

  useEffect(() => {
    if (isFocused && feedbackItemRef.current)
      feedbackItemRef.current.scrollIntoView({ block: 'center', inline: 'center' });
  }, [isFocused]);

  const handleEditClick = useCallback(() => {
    if (isFeedback) onEditFeedbackClick({ feedbackRecognitionItemUuid });
    else handleToggleKudosModal();
  }, [feedbackRecognitionItemUuid, handleToggleKudosModal, isFeedback, onEditFeedbackClick]);

  const creatorName = useMemo(() => (currentUserId === creator?.id ? 'You' : creator?.fullName || creator?.email), [
    creator,
    currentUserId,
  ]);

  const receiverName = useMemo(() => {
    if (!receiver) return getFirstName(workspaceName);
    return currentUserId === receiver.id ? 'you' : receiver.fullName || receiver.email;
  }, [receiver]);

  // This happens when the feedback has a creator / receiver in the database but we could not retrieve it on the front-end
  // For now, if either one of these is true, we won't show the component
  // Normalizing the reducer will remove the need for this
  const creatorExistsButNotRetrieved = feedbackRecognitionItem?.creatorId && !creator;
  const receiverExistsButNotRetrieved = feedbackRecognitionItem?.receiverId && !receiver;

  if (creatorExistsButNotRetrieved || receiverExistsButNotRetrieved) return null;
  if (!feedbackRecognitionItem) return null;

  return (
    <div className="feedback-item d-flex flex-column mt-4" ref={feedbackItemRef}>
      {isEditKudosModalOpen && (
        <GiveRecognitionModal isOpen feedback={feedbackRecognitionItem} onModalToggle={handleToggleKudosModal} />
      )}
      <div className="feedback-item-title d-flex align-items-center mb-2">
        <Avatar avatar={creator.avatar} height={28} />
        <div className="feedback-item-title-author text-muted mx-2">
          {`${creatorName} ${isFeedback ? `left feedback for ${receiverName}` : 'gave Kudos'} - ${dateMMMDDYYYY(
            feedbackRecognitionItem.createdAt
          )}`}
        </div>
        {currentUserId === feedbackRecognitionItem?.creatorId && (
          <OptionsButtons onEditClick={handleEditClick} onDeleteClick={handleDeleteClick} />
        )}
      </div>
      <div className={`feedback-item-card wrapper border ${focused ? 'feedback-item-card--focused' : ''}`}>
        <div className="d-flex justify-content-between feedback-item-card-title py-2 px-3 border-bottom">
          <b className="text-uppercase">
            {getTitle({
              requestType: feedbackRecognitionItem.requestType,
              currentUserId,
              creator,
              receiver,
              workspaceName,
            })}
          </b>
          {receiverAndCreatorAreInWorkspace && (
            <NotificationAndViewedBar feedback={feedbackRecognitionItem} currentUserId={currentUserId} />
          )}
        </div>
        {!receiver && (
          <Info
            type="warning"
            className="mx-3 mt-2 mb-3 d-flex flex-column flex-lg-row align-items-center justify-content-between py-1 "
          >
            Invite {receiverName} so they can see the Kudos you gave!
            <InviteTeammateButton
              workspaceId={workspaceUuid}
              teammateName={receiverName}
              workspaceTitle={workspaceName}
            />
          </Info>
        )}
        {isFeedback ? (
          <FeedbackContent feedbackItem={feedbackRecognitionItem} />
        ) : (
          <RecognitionContent feedback={feedbackRecognitionItem} currentUserId={currentUserId} />
        )}
        {!isFeedback && showReactions && (
          <div className="feedback-item-reactions">
            <Reactions reactionableId={uuid} reactionableType="feedback" />
          </div>
        )}
        <FeedbackCommentsArea
          sectionRef={sectionRef}
          feedbackUuid={feedbackRecognitionItem.uuid}
          workspaceUuid={feedbackRecognitionItem.workspaceUuid}
          currentUserId={currentUserId}
          creatorName={creator?.firstName || creator?.email}
          onGiveFeedback={onGiveFeedback}
          showGiveFeedbackButton={showGiveFeedbackButton}
          feedbackRecognitionRequestId={feedbackRecognitionRequestId}
          showEditButtonGroup={receiverAndCreatorAreInWorkspace}
        />
      </div>
    </div>
  );
}

export default memo(FeedbackItem);
